import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';

/**
 * Service for fetching pre-transformed room and structure data
 * These collections contain data with transforms already applied (absolute world coordinates)
 */

// Helper to clean Firebase-specific fields (but no data transformation)
const cleanFirebaseData = (doc) => {
  if (!doc) return null;

  // Create a copy
  const cleaned = Array.isArray(doc) ? [...doc] : { ...doc };

  Object.keys(cleaned).forEach((key) => {
    const value = cleaned[key];

    // 1. Firebase Timestamp
    if (value && value.toDate) {
      cleaned[key] = value.toDate().toISOString();
    }
    // 2. Firestore DocumentReference
    else if (value && value.firestore && value.path) {
      cleaned[key] = {
        _type: 'DocumentReference',
        path: value.path,
      };
    }
    // 3. Firestore GeoPoint
    else if (
      value &&
      value.latitude !== undefined &&
      value.longitude !== undefined &&
      value.constructor.name === 'GeoPoint'
    ) {
      cleaned[key] = {
        _type: 'GeoPoint',
        latitude: value.latitude,
        longitude: value.longitude,
      };
    }
    // 4. Firebase Storage Reference (if any URLs are stored as refs)
    else if (value && value.bucket && value.fullPath) {
      cleaned[key] = {
        _type: 'StorageReference',
        bucket: value.bucket,
        fullPath: value.fullPath,
      };
    }
    // 5. Handle undefined (which doesn't serialize to JSON)
    else if (value === undefined) {
      delete cleaned[key];
    }
    // 6. Handle NaN and Infinity
    else if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
      cleaned[key] = {
        _type: 'SpecialNumber',
        value: isNaN(value) ? 'NaN' : value > 0 ? 'Infinity' : '-Infinity',
      };
    }
    // Recursively clean nested objects and arrays
    else if (value && typeof value === 'object') {
      cleaned[key] = cleanFirebaseData(value);
    }
  });

  return cleaned;
};

// Export structure and rooms to JSON
export const exportStructureToJSON = async (structureId) => {
  try {
    // Always use pre-transformed collections
    const structure = await getPreTransformedStructure(structureId);
    if (!structure) {
      throw new Error(`Structure ${structureId} not found`);
    }

    const rooms = await getPreTransformedRooms(structureId);

    // Create export with NO transformations - just clean Firebase data
    const exportData = {
      exportMetadata: {
        version: '1.0',
        exportDate: new Date().toISOString(),
        source: 'better-building-firebase',
        collections: {
          structure: 'structures',
          rooms: 'rooms',
        },
        coordinateSystem: 'three-js-y-up',
        units: 'meters',
        dataFormat: 'pre-transformed',
      },
      structure: cleanFirebaseData(structure),
      rooms: rooms.map(cleanFirebaseData),
    };

    return JSON.stringify(exportData, null, 2);
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
};

// Check if a string is a valid ISO date string
const isISODateString = (str) => {
  if (typeof str !== 'string') return false;

  // ISO 8601 format: YYYY-MM-DDTHH:mm:ss.sssZ
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  if (!isoRegex.test(str)) return false;

  // Verify it's a valid date
  const date = new Date(str);
  return !isNaN(date.getTime());
};

// Convert special types back to Firebase objects
const restoreFirebaseTypes = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  // Clone the object to avoid mutations
  const restored = Array.isArray(obj) ? [...obj] : { ...obj };

  // List of known timestamp fields
  const timestampFields = [
    'createdAt',
    'updatedAt',
    'appliedAt',
    'lastModified',
    'modifiedAt',
    'timestamp',
  ];

  // Process each property
  Object.keys(restored).forEach((key) => {
    const value = restored[key];

    // 1. Check if this is a timestamp field with ISO string value
    if (
      timestampFields.includes(key) &&
      typeof value === 'string' &&
      isISODateString(value)
    ) {
      restored[key] = Timestamp.fromDate(new Date(value));
    }
    // 2. Handle special type objects
    else if (value && typeof value === 'object' && value._type) {
      switch (value._type) {
        case 'DocumentReference':
          // Note: Creating a proper DocumentReference requires the Firestore instance
          // For validation, we might just compare the path
          restored[key] = doc(db, value.path);
          break;

        case 'GeoPoint':
          // Better Building doesn't use GeoPoint, but included for completeness
          restored[key] = value; // Keep as is since we don't import GeoPoint
          break;

        case 'StorageReference':
          // Storage references can't be reconstructed easily
          // Keep as-is for comparison
          restored[key] = value;
          break;

        case 'SpecialNumber':
          if (value.value === 'NaN') restored[key] = NaN;
          else if (value.value === 'Infinity') restored[key] = Infinity;
          else if (value.value === '-Infinity') restored[key] = -Infinity;
          break;
      }
    }
    // 3. Recursively process nested objects and arrays
    else if (value && typeof value === 'object') {
      restored[key] = restoreFirebaseTypes(value);
    }
  });

  return restored;
};

// Deep comparison utility
const deepCompare = (obj1, obj2, path = '') => {
  const differences = [];

  // Handle null/undefined
  if (obj1 === obj2) return differences;
  if (!obj1 || !obj2) {
    differences.push({
      path,
      original: obj1,
      reimported: obj2,
    });
    return differences;
  }

  // Handle Timestamp comparison
  if (obj1 instanceof Timestamp && obj2 instanceof Timestamp) {
    if (
      obj1.seconds !== obj2.seconds ||
      obj1.nanoseconds !== obj2.nanoseconds
    ) {
      differences.push({
        path,
        type: 'timestamp_mismatch',
        original: obj1.toDate().toISOString(),
        reimported: obj2.toDate().toISOString(),
      });
    }
    return differences;
  }

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      differences.push({
        path,
        type: 'array_length_mismatch',
        original: obj1.length,
        reimported: obj2.length,
      });
    }

    const maxLength = Math.max(obj1.length, obj2.length);
    for (let i = 0; i < maxLength; i++) {
      differences.push(...deepCompare(obj1[i], obj2[i], `${path}[${i}]`));
    }
    return differences;
  }

  // Handle objects
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    // Compare all keys
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of allKeys) {
      const newPath = path ? `${path}.${key}` : key;

      if (!(key in obj1)) {
        differences.push({
          path: newPath,
          type: 'missing_in_original',
          reimported: obj2[key],
        });
      } else if (!(key in obj2)) {
        differences.push({
          path: newPath,
          type: 'missing_in_reimport',
          original: obj1[key],
        });
      } else {
        differences.push(...deepCompare(obj1[key], obj2[key], newPath));
      }
    }
    return differences;
  }

  // Compare primitive values
  if (obj1 !== obj2) {
    differences.push({
      path,
      type: 'value_mismatch',
      original: obj1,
      reimported: obj2,
    });
  }

  return differences;
};

// Validate JSON export by re-importing it
export const validateJSONExport = async (jsonFile) => {
  try {
    // 1. Parse JSON file
    const jsonText =
      typeof jsonFile === 'string' ? jsonFile : await jsonFile.text();
    const jsonData = JSON.parse(jsonText);
    const { structure, rooms } = jsonData;

    // 2. Convert ISO strings back to Firebase Timestamps
    const structureWithTimestamps = restoreFirebaseTypes(structure);
    const roomsWithTimestamps = rooms.map(restoreFirebaseTypes);

    // 3. Create temporary collections for validation
    const tempStructureId = `${structure.id}_validation_${Date.now()}`;
    const tempStructureRef = doc(
      db,
      'tempStructuresForValidation',
      tempStructureId
    );

    // 4. Write structure to temp collection
    await setDoc(tempStructureRef, structureWithTimestamps);

    // 5. Write rooms to temp collection with modified IDs
    const tempRoomIds = [];
    const roomWritePromises = roomsWithTimestamps.map((room) => {
      const tempRoomId = `${room.id}_validation_${Date.now()}`;
      tempRoomIds.push(tempRoomId);
      const tempRoom = {
        ...room,
        id: tempRoomId,
        structureId: tempStructureId,
      };
      return setDoc(doc(db, 'tempRoomsForValidation', tempRoomId), tempRoom);
    });
    await Promise.all(roomWritePromises);

    // 6. Read back from temp collections
    const tempStructureDoc = await getDoc(tempStructureRef);
    const tempStructureData = {
      id: tempStructureDoc.id,
      ...tempStructureDoc.data(),
    };

    const tempRoomsData = [];
    for (const roomId of tempRoomIds) {
      const roomDoc = await getDoc(doc(db, 'tempRoomsForValidation', roomId));
      if (roomDoc.exists()) {
        tempRoomsData.push({ id: roomDoc.id, ...roomDoc.data() });
      }
    }

    // 7. Compare with original data (excluding ID fields since we modified them)
    // Remove ID fields before comparison
    const { id: origStructId, ...structureWithoutId } = structureWithTimestamps;
    const { id: tempStructId, ...tempStructureWithoutId } = tempStructureData;

    const structureComparison = deepCompare(
      structureWithoutId,
      tempStructureWithoutId
    );

    const roomComparisons = [];
    for (let i = 0; i < roomsWithTimestamps.length; i++) {
      const originalRoom = roomsWithTimestamps[i];
      const tempRoom = tempRoomsData[i];
      if (tempRoom) {
        // Remove id and structureId fields before comparison
        const {
          id: origId,
          structureId: origStructureId,
          ...originalRoomWithoutIds
        } = originalRoom;
        const {
          id: tempId,
          structureId: tempStructureId,
          ...tempRoomWithoutIds
        } = tempRoom;

        const comparison = deepCompare(
          originalRoomWithoutIds,
          tempRoomWithoutIds
        );
        roomComparisons.push(...comparison);
      }
    }

    // 8. Clean up temp collections
    await deleteDoc(tempStructureRef);
    await Promise.all(
      tempRoomIds.map((roomId) =>
        deleteDoc(doc(db, 'tempRoomsForValidation', roomId))
      )
    );

    // 9. Filter out acceptable differences
    const isAcceptableDifference = (diff) => {
      // Fields that were undefined in Firebase are expected to be missing in JSON
      if (diff.type === 'missing_in_reimport' && diff.original === undefined) {
        return true;
      }

      // Small floating point differences are acceptable
      if (
        diff.type === 'value_mismatch' &&
        typeof diff.original === 'number' &&
        typeof diff.reimported === 'number'
      ) {
        return Math.abs(diff.original - diff.reimported) < 0.000001;
      }

      // Firestore server-side fields that might differ slightly
      if (diff.path.includes('_version') || diff.path.includes('_internalId')) {
        return true;
      }

      return false;
    };

    const allDifferences = [...structureComparison, ...roomComparisons];
    const unacceptableDifferences = allDifferences.filter(
      (diff) => !isAcceptableDifference(diff)
    );
    const acceptableDifferences = allDifferences.filter((diff) =>
      isAcceptableDifference(diff)
    );

    const isValid = unacceptableDifferences.length === 0;

    return {
      isValid,
      differences: unacceptableDifferences,
      acceptableDifferences,
      summary: {
        totalDifferences: unacceptableDifferences.length,
        structureDifferences: structureComparison.filter(
          (d) => !isAcceptableDifference(d)
        ).length,
        roomDifferences: roomComparisons.filter(
          (d) => !isAcceptableDifference(d)
        ).length,
        acceptableDifferences: acceptableDifferences.length,
      },
    };
  } catch (error) {
    console.error('Validation failed:', error);
    throw error;
  }
};

// Fetch a single structure from the new 'structures' collection
export const getPreTransformedStructure = async (structureId) => {
  try {
    const docRef = doc(db, 'structures', structureId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { ...docSnap.data(), id: docSnap.id };
    } else {
      console.error(
        'No such structure in pre-transformed collection:',
        structureId
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching pre-transformed structure:', error);
    throw error;
  }
};

// Fetch all rooms for a structure from the new 'rooms' collection
export const getPreTransformedRooms = async (structureId) => {
  try {
    // Try with 'structureId' field first
    let roomsQuery = query(
      collection(db, 'rooms'),
      where('structureId', '==', structureId)
    );

    let querySnapshot = await getDocs(roomsQuery);

    // If no results, try with 'structureID' (capital ID)
    if (querySnapshot.empty) {
      roomsQuery = query(
        collection(db, 'rooms'),
        where('structureID', '==', structureId)
      );
      querySnapshot = await getDocs(roomsQuery);
    }

    const rooms = [];
    querySnapshot.forEach((doc) => {
      const roomData = doc.data();

      // Check if any objects might be doors
      if (roomData.objects && roomData.objects.length > 0) {
        // First check with original criteria
        const possibleDoors = roomData.objects.filter((obj) => {
          const hasDoorCategory = obj.category?.door;
          const hasThinDimensions = obj.dimensions && obj.dimensions.z < 0.5;
          const hasReasonableWidth =
            obj.dimensions && obj.dimensions.x > 0.6 && obj.dimensions.x < 1.5;
          return hasDoorCategory || (hasThinDimensions && hasReasonableWidth);
        });

        // Also check for furniture-categorized items that might be doors
        const furnitureWithDoorDimensions = roomData.objects.filter((obj) => {
          const hasFurnitureCategory =
            obj.category?.furniture ||
            obj.category?.chair ||
            obj.category?.table;
          const doorLikeHeight =
            obj.dimensions && obj.dimensions.y > 1.8 && obj.dimensions.y < 2.5;
          const doorLikeWidth =
            obj.dimensions && obj.dimensions.x > 0.6 && obj.dimensions.x < 1.5;
          const doorLikeThickness = obj.dimensions && obj.dimensions.z < 0.25;
          return (
            hasFurnitureCategory &&
            doorLikeHeight &&
            doorLikeWidth &&
            doorLikeThickness
          );
        });

        if (
          possibleDoors.length > 0 ||
          furnitureWithDoorDimensions.length > 0
        ) {
          // console.log(`%c🚪 Found possible doors in objects array!`, 'background: #e74c3c; color: white; padding: 2px 6px', {
          //   roomId: doc.id,
          //   withDoorCategory: possibleDoors.length,
          //   withFurnitureCategory: furnitureWithDoorDimensions.length,
          //   possibleDoors: possibleDoors.map(d => ({
          //     id: d.id,
          //     category: d.category,
          //     dimensions: d.dimensions,
          //     attributes: d.attributes
          //   })),
          //   furnitureWithDoorDimensions: furnitureWithDoorDimensions.map(d => ({
          //     id: d.id,
          //     category: d.category,
          //     dimensions: d.dimensions,
          //     attributes: d.attributes,
          //     parentWallIds: d.parentWallIds
          //   }))
          // });
        }
      }
      rooms.push({ ...roomData, id: doc.id });
    });
    return rooms;
  } catch (error) {
    console.error('Error fetching pre-transformed rooms:', error);
    // If orderBy fails due to missing index, try without it
    if (error.code === 'failed-precondition') {
      try {
        const roomsQuery = query(
          collection(db, 'rooms'),
          where('structureId', '==', structureId)
        );
        const querySnapshot = await getDocs(roomsQuery);
        const rooms = [];
        querySnapshot.forEach((doc) => {
          rooms.push({ ...doc.data(), id: doc.id });
        });
        return rooms;
      } catch (retryError) {
        console.error('Retry also failed:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};

// Fetch a single room by ID
export const getPreTransformedRoom = async (roomId) => {
  try {
    const docRef = doc(db, 'rooms', roomId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { ...docSnap.data(), id: docSnap.id };
    } else {
      console.error('No such room in pre-transformed collection:', roomId);
      return null;
    }
  } catch (error) {
    console.error('Error fetching pre-transformed room:', error);
    throw error;
  }
};

// Fetch both structure and its rooms
export const getPreTransformedStructureWithRooms = async (structureId) => {
  try {
    const [structure, rooms] = await Promise.all([
      getPreTransformedStructure(structureId),
      getPreTransformedRooms(structureId),
    ]);

    return {
      structure,
      rooms,
    };
  } catch (error) {
    console.error(
      'Error fetching pre-transformed structure with rooms:',
      error
    );
    throw error;
  }
};

// Fetch rooms directly from server (bypasses cache)
// Use this after server-side updates to ensure fresh data
export const getPreTransformedRoomsFromServer = async (structureId) => {
  try {
    // Try with 'structureId' field first
    let roomsQuery = query(
      collection(db, 'rooms'),
      where('structureId', '==', structureId)
    );

    let querySnapshot = await getDocsFromServer(roomsQuery);

    // If no results, try with 'structureID' (capital ID)
    if (querySnapshot.empty) {
      roomsQuery = query(
        collection(db, 'rooms'),
        where('structureID', '==', structureId)
      );
      querySnapshot = await getDocsFromServer(roomsQuery);
    }

    const rooms = [];
    querySnapshot.forEach((doc) => {
      rooms.push({ ...doc.data(), id: doc.id });
    });
    return rooms;
  } catch (error) {
    console.error('Error fetching rooms from server:', error);
    throw error;
  }
};

// Fetch structure and rooms directly from server (bypasses cache)
// Use this after server-side updates to ensure fresh data
export const getPreTransformedStructureWithRoomsFromServer = async (
  structureId
) => {
  try {
    // Fetch structure from server
    const docRef = doc(db, 'structures', structureId);
    const docSnap = await getDocFromServer(docRef);
    const structure = docSnap.exists()
      ? { ...docSnap.data(), id: docSnap.id }
      : null;

    // Fetch rooms from server
    const rooms = await getPreTransformedRoomsFromServer(structureId);

    return {
      structure,
      rooms,
    };
  } catch (error) {
    console.error(
      'Error fetching pre-transformed structure with rooms from server:',
      error
    );
    throw error;
  }
};

// Fetch all structures for a project
export const getPreTransformedStructuresByProjectId = async (projectId) => {
  try {
    const structuresQuery = query(
      collection(db, 'structures'),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(structuresQuery);
    const structures = [];

    querySnapshot.forEach((doc) => {
      structures.push({ ...doc.data(), id: doc.id });
    });

    return structures;
  } catch (error) {
    console.error(
      'Error fetching pre-transformed structures for project:',
      error
    );
    throw error;
  }
};

// Fetch all rooms for multiple structures
export const getPreTransformedRoomsForMultipleStructures = async (
  structureIds
) => {
  if (!structureIds || structureIds.length === 0) {
    return [];
  }

  try {
    // Try with 'structureId' field first
    // Firestore 'in' query has a limit of 10 items, so we need to batch
    const batches = [];
    for (let i = 0; i < structureIds.length; i += 10) {
      const batch = structureIds.slice(i, i + 10);
      const roomsQuery = query(
        collection(db, 'rooms'),
        where('structureId', 'in', batch)
      );
      batches.push(getDocs(roomsQuery));
    }

    const results = await Promise.all(batches);
    const rooms = [];

    results.forEach((querySnapshot) => {
      querySnapshot.forEach((doc) => {
        rooms.push({ ...doc.data(), id: doc.id });
      });
    });

    // If no results, try with 'structureID' (capital ID)
    if (rooms.length === 0) {
      const batchesID = [];
      for (let i = 0; i < structureIds.length; i += 10) {
        const batch = structureIds.slice(i, i + 10);
        const roomsQuery = query(
          collection(db, 'rooms'),
          where('structureID', 'in', batch)
        );
        batchesID.push(getDocs(roomsQuery));
      }

      const resultsID = await Promise.all(batchesID);

      resultsID.forEach((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          rooms.push({ ...doc.data(), id: doc.id });
        });
      });
    }

    return rooms;
  } catch (error) {
    console.error(
      'Error fetching pre-transformed rooms for multiple structures:',
      error
    );

    // If orderBy fails due to missing index, try without it
    if (error.code === 'failed-precondition') {
      try {
        const batches = [];
        for (let i = 0; i < structureIds.length; i += 10) {
          const batch = structureIds.slice(i, i + 10);
          const roomsQuery = query(
            collection(db, 'rooms'),
            where('structureId', 'in', batch)
          );
          batches.push(getDocs(roomsQuery));
        }

        const results = await Promise.all(batches);
        const rooms = [];

        results.forEach((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            rooms.push({ ...doc.data(), id: doc.id });
          });
        });

        return rooms;
      } catch (retryError) {
        console.error('Retry also failed:', retryError);
        // Try fetching rooms one structure at a time
        const allRooms = [];
        for (const structureId of structureIds) {
          try {
            const rooms = await getPreTransformedRooms(structureId);
            allRooms.push(...rooms);
          } catch (err) {
            console.error(
              `Failed to fetch rooms for structure ${structureId}:`,
              err
            );
          }
        }
        return allRooms;
      }
    }
    throw error;
  }
};
